import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "./ui/button";
import { MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    q: "Is my business data private? Does your AI train on my messages?",
    a: "Absolutely. We employ a strict Zero-Knowledge Architecture. This means your data is encrypted both in transit and at rest. Most importantly, we do not use your private business communications to train our public AI models. Your trade secrets, client details, and internal strategies remain 100% yours.",
  },
  {
    q: "Will the AI send messages automatically without me checking?",
    a: "By default, no. ClearMakes is designed to keep you in control. It intelligently analyzes your inbox (especially in Gmail) to detect exactly which messages require a response and which are just noise.\n\nIt operates in \"Draft Mode,\" preparing the perfect reply for you to review. You simply click \"Send\" or \"Edit.\"\n\nYou have full control: You can manually toggle \"Auto-Pilot\" ON or OFF in your settings. If turned ON, the AI can handle replies automatically, but only for the specific platforms or contacts you select.",
  },
  {
    q: "What platforms do you currently support?",
    a: "We currently support Gmail, Microsoft Teams, Slack, Facebook Messenger, and Instagram DMs. We are actively working on adding more integrations to our Unified Dashboard soon.",
  },
  {
    q: "How accurate is the AI at understanding context?",
    a: "ClearMakes understands context by sender. If a sender is already in your summaries or history, we use how you've replied to them before—plus your Knowledge Base (FAQs) and any custom or saved replies for that contact—to match your tone and style. So drafts are tailored to each relationship: same tone you use with that person, and easier one-click replies.",
  },
  {
    q: "What happens if I cancel my subscription?",
    a: "You can cancel at any time with one click from your dashboard—no phone calls required.\n\nIf you are on a Paid Plan: You retain full access to all features until the end of your billing cycle.\n\nIf you downgrade to Free: You will still have access to your account, but your searchable message history will be limited to the last 3 days, and premium automation features will be locked.",
  },
  {
    q: "Can I use this for my personal email, or is it just for business?",
    a: "While you can connect personal accounts, ClearMakes is optimized for business workflows. Our features—like Priority Hub, Calendar Sync, and Deal Stacking—are designed to help executives, freelancers, and sales teams save time.",
  },
  {
    q: "We are a large team. Do you offer SSO and centralized billing?",
    a: "Yes. For organizations with multiple seats, our Enterprise Plan offers Single Sign-On (SSO) via Okta, Google Workspace, or Microsoft Azure. We also offer centralized invoicing, role-based access control, and a dedicated account manager for onboarding. Please click \"Contact Sales\" for a custom quote.",
  },
];

export const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="faq" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="container relative z-10" ref={ref}>
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-lg mb-4">Everything you need to know about ClearMakes.</h2>
          <p className="body-lg max-w-xl mx-auto text-muted-foreground mb-8">
            Can&apos;t find the answer you&apos;re looking for? Chat with our support team.
          </p>
          <Button variant="glass" size="lg" asChild>
            <Link to="/contact" className="inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Contact Support
            </Link>
          </Button>
        </motion.div>

        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="glass-panel rounded-xl border border-white/10 px-6 data-[state=open]:border-primary/20"
              >
                <AccordionTrigger className="text-left py-5 hover:no-underline [&[data-state=open]]:text-primary">
                  <span className="font-medium pr-4">{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
